/**
 * about-scroll.js
 * Mobile-only sticky scroll — two-phase fade per panel (text → image).
 *
 * Follows the exact pattern from example/index.html:
 *   - container height = sections.length * 2 * 180vh
 *   - progress = (-rect.top) / rect.height, clamped 0–1
 *   - step = Math.floor(progress * steps)  where steps = sections.length * 2
 *   - step === i*2       → show text block  (opacity:1, translateY(0))
 *   - step === i*2+1     → show image block (opacity:1, carousel stays position:absolute inset:0)
 *   - all others         → section zIndex:0, both blocks hidden
 *
 * HTML structure per panel (about-story-panels.liquid):
 *   <section class="au-story__step">
 *     <div class="au-story__step-inner">
 *       <div class="au-text-block ...">...</div>
 *     </div>
 *     <div class="au-image-block au-image-block--carousel|--single">...</div>
 *   </section>
 */

(function () {
  'use strict';

  var MOBILE_BP = 1024;
  var headerSyncCleanup = null;

  function isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  function initHeaderOffsetSync() {
    var header = document.querySelector('.section-header');
    var ticking = false;

    function sync() {
      ticking = false;

      if (!header) {
        document.documentElement.style.setProperty('--au-header-offset', '0px');
        return;
      }

      var isVisibleSticky =
        header.classList.contains('shopify-section-header-sticky') &&
        !header.classList.contains('shopify-section-header-hidden');

      document.documentElement.style.setProperty(
        '--au-header-offset',
        isVisibleSticky ? header.offsetHeight + 'px' : '0px'
      );
    }

    function requestSync() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    }

    sync();
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);

    var observer = header
      ? new MutationObserver(requestSync)
      : null;

    if (observer) {
      observer.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    return function cleanup() {
      window.removeEventListener('scroll', requestSync);
      window.removeEventListener('resize', requestSync);
      if (observer) observer.disconnect();
      document.documentElement.style.setProperty('--au-header-offset', '0px');
    };
  }

  /* ── Sticky-scroll controller (mobile only) ──────────── */
  function StickyScroll(container) {
    this.container = container;
    this.wrap = container.querySelector('.au-story__sticky-wrap');
    this.sections = Array.from(container.querySelectorAll('.au-story__step'));
    this.metrics = [];
    this.totalUnits = 0;
    this._bound = this._onScroll.bind(this);
    this._resizeBound = this._onResize.bind(this);
    this._raf = null;
    this._enabled = false;
    this.init();
  }

  StickyScroll.prototype.init = function () {
    this._setHeight();
    if (isMobile()) {
      this._enable();
    }
    window.addEventListener('resize', this._resizeBound);
  };

  StickyScroll.prototype._setHeight = function () {
    if (!isMobile() || this.sections.length === 0 || !this.wrap) {
      this.metrics = [];
      this.totalUnits = 0;
      this.container.style.height = '';
      return;
    }

    this._measureSections();

    var viewportHeight = this.wrap.clientHeight || window.innerHeight || 1;
    /* Travel distance should equal total units * viewport height. */
    this.container.style.height = Math.ceil((this.totalUnits + 1) * viewportHeight) + 'px';
  };

  StickyScroll.prototype._measureSections = function () {
    var wrapHeight = this.wrap ? this.wrap.clientHeight || window.innerHeight : window.innerHeight;
    var totalUnits = 0;

    this.metrics = this.sections.map(function (section) {
      var text = section.querySelector('.au-text-block');
      var image = section.querySelector('.au-image-block');
      var inner = section.querySelector('.au-story__step-inner');
      var innerStyles = inner ? window.getComputedStyle(inner) : null;
      var paddingTop = innerStyles ? parseFloat(innerStyles.paddingTop) || 0 : 0;
      var paddingBottom = innerStyles ? parseFloat(innerStyles.paddingBottom) || 0 : 0;
      var availableTextHeight = Math.max(wrapHeight - paddingTop - paddingBottom, wrapHeight * 0.45);
      var textHeight = text ? text.scrollHeight : 0;
      var textScroll = Math.max(textHeight - availableTextHeight, 0);
      var textUnits = text ? Math.max(1, 1 + textScroll / Math.max(wrapHeight, 1)) : 0;
      var imageUnits = image ? 1 : 0;
      var metric = {
        section: section,
        text: text,
        image: image,
        inner: inner,
        textScroll: textScroll,
        textUnits: textUnits,
        imageUnits: imageUnits,
        start: totalUnits,
        end: totalUnits + textUnits + imageUnits
      };

      totalUnits = metric.end;
      return metric;
    });

    this.totalUnits = totalUnits;
  };

  StickyScroll.prototype._enable = function () {
    if (!this._enabled) {
      window.addEventListener('scroll', this._bound, { passive: true });
      this._enabled = true;
    }
    /* Always recalculate immediately (handles first paint + resize) */
    this._calc();
  };

  StickyScroll.prototype._disable = function () {
    if (this._enabled) {
      window.removeEventListener('scroll', this._bound);
      this._enabled = false;
    }
    /* Reset all inline styles so desktop layout takes over cleanly */
    this.sections.forEach(function (section) {
      section.style.zIndex = '';
      var text = section.querySelector('.au-text-block');
      var image = section.querySelector('.au-image-block');
      var inner = section.querySelector('.au-story__step-inner');
      if (text) {
        text.style.opacity = '';
        text.style.transform = '';
      }
      if (image) {
        image.style.opacity = '';
        image.style.transform = '';
      }
      if (inner) {
        inner.style.transform = '';
      }
    });
  };

  StickyScroll.prototype._onResize = function () {
    this._setHeight();
    if (isMobile()) {
      this._enable();
    } else {
      this._disable();
    }
  };

  StickyScroll.prototype._onScroll = function () {
    if (this._raf) return;
    var self = this;
    this._raf = requestAnimationFrame(function () {
      self._raf = null;
      self._calc();
    });
  };

  StickyScroll.prototype._calc = function () {
    if (!this.wrap || !this.metrics.length || this.totalUnits <= 0) return;

    var rect = this.container.getBoundingClientRect();
    var wrapHeight = this.wrap.clientHeight || window.innerHeight || 1;
    var travel = Math.max(rect.height - wrapHeight, 1);
    var scrolled = Math.min(Math.max(-rect.top, 0), travel);
    var unitPos = (scrolled / travel) * this.totalUnits;

    this.metrics.forEach(function (metric) {
      var section = metric.section;
      var text = metric.text;
      var image = metric.image;
      var inner = metric.inner;
      var isCarousel = image && image.classList.contains('au-image-block--carousel');
      var inTextPhase = unitPos >= metric.start && unitPos < metric.start + metric.textUnits;
      var inImagePhase = image && unitPos >= metric.start + metric.textUnits && unitPos <= metric.end;

      section.style.zIndex = '0';

      if (inner) {
        inner.style.transform = 'translateY(0)';
      }

      if (text) {
        text.style.opacity = '0';
        text.style.transform = 'translateY(40px)';
      }

      if (image) {
        image.style.opacity = '0';
        if (!isCarousel) {
          image.style.transform = 'translateY(-40%)';
        }
      }

      if (inTextPhase) {
        var textProgress = metric.textUnits > 0 ? (unitPos - metric.start) / metric.textUnits : 0;

        section.style.zIndex = '10';

        if (text) {
          text.style.opacity = '1';
          text.style.transform = 'translateY(0)';
        }

        if (inner && metric.textScroll > 0) {
          inner.style.transform = 'translateY(' + (-metric.textScroll * textProgress) + 'px)';
        }
      } else if (inImagePhase) {
        section.style.zIndex = '10';

        if (image) {
          image.style.opacity = '1';
          if (!isCarousel) {
            image.style.transform = 'translateY(-50%)';
          }
        }
      }
    });
  };

  StickyScroll.prototype.destroy = function () {
    window.removeEventListener('resize', this._resizeBound);
    this._disable();
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    this.container.style.height = '';
  };

  /* ── Desktop info overlay controller ────────────────── */
  function initDesktopInfoOverlay() {
    if (typeof window === 'undefined') return;
    
    document.querySelectorAll('.au-story__desk-img').forEach(function(imgContainer) {
      var toggle = imgContainer.querySelector('[data-desk-info-toggle]');
      if (!toggle) return;
      
      var overlayText = imgContainer.getAttribute('data-overlay-text') || '';
      var isOpen = false;
      
      function syncState() {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen && overlayText) {
          var existingOverlay = imgContainer.querySelector('.au-story__desk-info-overlay');
          if (!existingOverlay) {
            var overlay = document.createElement('div');
            overlay.className = 'au-story__desk-info-overlay';
            overlay.innerHTML = '<p class="au-story__desk-info-text">' + overlayText + '</p>';
            imgContainer.appendChild(overlay);
          }
        } else {
          var overlay = imgContainer.querySelector('.au-story__desk-info-overlay');
          if (overlay) overlay.remove();
        }
      }
      
      toggle.addEventListener('click', function() {
        isOpen = !isOpen;
        syncState();
      });
    });
  }

  /* ── Dynamic bento layout for 3 images ───────────────── */
  function initDynamicBentoLayout() {
    var THRESHOLD_PX = 250; // Adjust this value to change when layout switches
    
    document.querySelectorAll('.au-story__desk-panel').forEach(function(panel) {
      var media = panel.querySelector('.au-story__desk-media');
      var textCol = panel.querySelector('.au-story__desk-text');
      if (!media || !textCol) return;
      
      var imgCount = parseInt(media.getAttribute('data-img-count'), 10);
      if (imgCount !== 3) return;
      
      var textHeight = textCol.offsetHeight;
      var ratio = textHeight / imgCount;
      
      if (ratio < THRESHOLD_PX) {
        media.classList.add('au-story__desk-media--compact');
      } else {
        media.classList.add('au-story__desk-media--expanded');
      }
    });
  }

  /* ── Carousel controller (Swiper) ────────────────────── */
  function initCarouselInfoOverlay(shell, sw) {
    if (!shell) return null;

    var toggle = shell.querySelector('[data-au-info-toggle]');
    var overlay = shell.querySelector('[data-au-info-overlay]');
    var textNode = shell.querySelector('[data-au-info-text]');
    if (!toggle || !overlay || !textNode) return null;

    var slides = Array.from(sw.slides).filter(function (slide) {
      return !slide.classList.contains('swiper-slide-duplicate');
    });

    if (!slides.length) {
      slides = Array.from(shell.querySelectorAll('.swiper-wrapper > .swiper-slide'));
    }

    var isOpen = false;

    function currentText() {
      var slide = slides[sw.realIndex] || slides[0];
      return slide ? (slide.getAttribute('data-overlay-text') || '').trim() : '';
    }

    function syncContent() {
      var text = currentText();
      textNode.textContent = text;
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      overlay.hidden = !isOpen;
    }

    function onToggle() {
      isOpen = !isOpen;
      syncContent();
    }

    toggle.addEventListener('click', onToggle);
    syncContent();

    return {
      sync: syncContent,
      destroy: function () {
        toggle.removeEventListener('click', onToggle);
      }
    };
  }

  function initCarousels(root) {
    if (typeof Swiper === 'undefined') return [];

    var carousels = root.querySelectorAll('[data-au-carousel]');
    var instances = [];

    carousels.forEach(function (el) {
      var shell = el.closest('.au-story__carousel-shell');
      var paginationEl = shell ? shell.querySelector('.swiper-pagination') : null;
      var nextEl = shell ? shell.querySelector('.about-story__nav--next') : null;
      var prevEl = shell ? shell.querySelector('.about-story__nav--prev') : null;
      var sw = new Swiper(el, {
        slidesPerView: 1,
        // spaceBetween: 16,
        loop: true,
        grabCursor: true,
        autoplay: {
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: false
        },
        speed: 4500,
        allowTouchMove: true,
        touchStartPreventDefault: false,
        pagination: paginationEl ? {
          el: paginationEl,
          clickable: true
        } : false,
        navigation: nextEl && prevEl ? {
          nextEl: nextEl,
          prevEl: prevEl
        } : false,
        /* Re-calc slide widths when parent becomes visible (starts at opacity:0) */
        observer: true,
        observeParents: true
      });
      var infoOverlay = initCarouselInfoOverlay(shell, sw);
      el.style.setProperty('--swiper-wrapper-transition-timing-function', 'linear');

      /* Pause on hold (touch / pointer) */
      function pauseAutoplay() {
        if (sw.autoplay && sw.autoplay.running) sw.autoplay.stop();
      }
      function resumeAutoplay() {
        if (sw.autoplay && !sw.autoplay.running) sw.autoplay.start();
      }

      el.addEventListener('pointerdown', pauseAutoplay);
      el.addEventListener('touchstart', pauseAutoplay, { passive: true });
      el.addEventListener('pointerup', resumeAutoplay);
      el.addEventListener('touchend', resumeAutoplay);
      el.addEventListener('pointerleave', resumeAutoplay);

      sw.on('init', function () {
        if (infoOverlay) infoOverlay.sync();
      });
      sw.on('slideChange', function () {
        if (infoOverlay) infoOverlay.sync();
      });
      sw.on('update', function () {
        if (infoOverlay) infoOverlay.sync();
      });

      sw.__auCleanup = function () {
        el.removeEventListener('pointerdown', pauseAutoplay);
        el.removeEventListener('touchstart', pauseAutoplay);
        el.removeEventListener('pointerup', resumeAutoplay);
        el.removeEventListener('touchend', resumeAutoplay);
        el.removeEventListener('pointerleave', resumeAutoplay);
        if (infoOverlay) infoOverlay.destroy();
      };

      instances.push(sw);
    });

    return instances;
  }

  function destroyCarousels(instances) {
    instances.forEach(function (sw) {
      if (sw.__auCleanup) sw.__auCleanup();
      sw.destroy(true, true);
    });
  }

  /* ── Boot ─────────────────────────────────────────────── */
  var stickyInstances = [];
  var swiperInstances = [];

  function bootAll() {
    if (!headerSyncCleanup) {
      headerSyncCleanup = initHeaderOffsetSync();
    }

    document.querySelectorAll('[data-sticky-sections]').forEach(function (el) {
      stickyInstances.push(new StickyScroll(el));
    });

    document.querySelectorAll('.au-story').forEach(function (root) {
      swiperInstances = swiperInstances.concat(initCarousels(root));
    });

    initDesktopInfoOverlay();
    initDynamicBentoLayout();
  }

  function destroyAll() {
    stickyInstances.forEach(function (s) { s.destroy(); });
    stickyInstances = [];
    destroyCarousels(swiperInstances);
    swiperInstances = [];
    if (headerSyncCleanup) {
      headerSyncCleanup();
      headerSyncCleanup = null;
    }
  }

  function waitForSwiper(cb, attempts) {
    if (typeof Swiper !== 'undefined') { cb(); return; }
    if (attempts > 30) { cb(); return; }
    setTimeout(function () { waitForSwiper(cb, (attempts || 0) + 1); }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForSwiper(bootAll); });
  } else {
    waitForSwiper(bootAll);
  }

  /* Shopify theme editor */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      destroyAll();
      waitForSwiper(bootAll);
    });
    document.addEventListener('shopify:section:unload', function () {
      destroyAll();
    });
  }
})();
